<!--
  Copyright (C) 2022 - Jeci SARL - https://jeci.fr

  This program is free software: you can redistribute it and/or modify
  it under the terms of the GNU Affero General Public License as
  published by the Free Software Foundation, either version 3 of the
  License, or (at your option) any later version.

  This program is distributed in the hope that it will be useful,
  but WITHOUT ANY WARRANTY; without even the implied warranty of
  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
  GNU Affero General Public License for more details.

  You should have received a copy of the GNU Affero General Public License
  along with this program.  If not, see https://www.gnu.org/licenses/agpl-3.0.html.
-->

<template>
  <ul class="sidebar-menu">
    <template v-for="(item, index) in items" :key="item.labelKey || item.label">
      <!-- Section with subitems (accordion) -->
      <li v-if="item.items && item.items.length > 0" class="menu-section">
        <div
          class="menu-section-header"
          :class="{
            'has-active-child': hasActiveChild(item),
            'is-active': isActiveSection(item),
          }"
        >
          <router-link
            v-if="item.to"
            :to="item.to"
            class="menu-section-link"
            v-tooltip.right="getLabel(item)"
          >
            <i :class="item.icon" class="menu-icon"></i>
            <span class="menu-label">{{ getLabel(item) }}</span>
          </router-link>
          <template v-else>
            <i :class="item.icon" class="menu-icon"></i>
            <span class="menu-label">{{ getLabel(item) }}</span>
          </template>
          <button
            class="menu-toggle-btn"
            @click="toggleSection(index)"
            v-tooltip.right="isSlimMode ? getLabel(item) : null"
          >
            <i
              class="menu-arrow ri-arrow-down-s-line"
              :class="{ expanded: isExpanded(index) }"
            ></i>
          </button>
        </div>

        <!-- Expandable subitems -->
        <transition name="submenu">
          <ul v-show="isExpanded(index) && !isSlimMode" class="menu-subitems">
            <li
              v-for="subitem in item.items"
              :key="subitem.labelKey || subitem.label"
              class="menu-item"
              :class="{ active: isActive(subitem) }"
            >
              <router-link
                :to="subitem.to"
                class="menu-link"
                v-tooltip.right="getLabel(subitem)"
              >
                <i :class="subitem.icon" class="menu-icon"></i>
                <span class="menu-label">{{ getLabel(subitem) }}</span>
                <span v-if="subitem.badge" class="menu-badge">{{
                  subitem.badge
                }}</span>
              </router-link>
            </li>
          </ul>
        </transition>
      </li>

      <!-- Simple menu item (no subitems) -->
      <li
        v-else
        class="menu-item"
        :class="{ active: isActive(item), disabled: item.disabled }"
      >
        <router-link
          v-if="!item.disabled && item.to"
          :to="item.to"
          class="menu-link"
          v-tooltip.right="isSlimMode ? getLabel(item) : null"
        >
          <i :class="item.icon" class="menu-icon"></i>
          <span class="menu-label">{{ getLabel(item) }}</span>
          <span v-if="item.badge" class="menu-badge">{{ item.badge }}</span>
        </router-link>
        <span
          v-else
          class="menu-link disabled"
          v-tooltip.right="isSlimMode ? getLabel(item) : null"
        >
          <i :class="item.icon" class="menu-icon"></i>
          <span class="menu-label">{{ getLabel(item) }}</span>
          <span v-if="item.badge" class="menu-badge">{{ item.badge }}</span>
        </span>
      </li>
    </template>
  </ul>
</template>

<script>
import { ref, computed, watch } from "vue";
import { useRoute } from "vue-router";
import { useI18n } from "vue-i18n";
import { useLayout } from "@/composables/useLayout";

/**
 * Check if a section contains the active route
 * @param {Object} section - Menu section
 * @param {string} currentPath - Current route path
 * @returns {boolean}
 */
function sectionContainsActivePath(section, currentPath) {
  if (section.to === currentPath) {
    return true;
  }
  if (section.items) {
    return section.items.some((item) => item.to === currentPath);
  }
  return false;
}

export default {
  name: "AppSubmenu",
  props: {
    /**
     * Menu items array
     * Each item can have: { label, labelKey, icon, to, badge, items }
     * - label: Display text (used if labelKey not provided)
     * - labelKey: i18n translation key
     * - icon: Icon class (e.g., 'ri-home-line')
     * - to: Route path
     * - badge: Optional badge text
     * - items: Array of sub-items (same structure)
     */
    items: {
      type: Array,
      default: () => [],
    },
  },
  setup(props) {
    const route = useRoute();
    const { t } = useI18n();
    const { layoutState, isSlim, isSlimPlus, isDrawer, isSmallScreen } =
      useLayout();
    const expandedSections = ref([]);

    const STORAGE_KEY = "pristy-menu-expanded-sections";

    // Get item key for localStorage (use key, labelKey, or label)
    const getItemKey = (item) => {
      return item.key || item.labelKey || item.label;
    };

    // Load expanded state from localStorage
    const loadExpandedState = () => {
      try {
        const saved = localStorage.getItem(STORAGE_KEY);
        if (saved) {
          const savedKeys = JSON.parse(saved);
          // Convert saved keys back to indices
          props.items.forEach((item, index) => {
            if (item.items && savedKeys.includes(getItemKey(item))) {
              if (!expandedSections.value.includes(index)) {
                expandedSections.value.push(index);
              }
            }
          });
        }
      } catch (e) {
        console.warn("Failed to load menu state from localStorage:", e);
      }
    };

    // Save expanded state to localStorage
    const saveExpandedState = () => {
      try {
        const expandedKeys = expandedSections.value
          .map((index) => props.items[index])
          .filter(Boolean)
          .map(getItemKey);
        localStorage.setItem(STORAGE_KEY, JSON.stringify(expandedKeys));
      } catch (e) {
        console.warn("Failed to save menu state to localStorage:", e);
      }
    };

    // Initialize expanded sections based on current route
    const initExpandedSections = () => {
      props.items.forEach((item, index) => {
        if (item.items && sectionContainsActivePath(item, route.path)) {
          if (!expandedSections.value.includes(index)) {
            expandedSections.value.push(index);
            saveExpandedState();
          }
        }
      });
    };

    // Load saved state on mount, then init based on route
    loadExpandedState();

    // Watch for route changes to expand active sections
    watch(
      () => route.path,
      () => {
        initExpandedSections();
      },
      { immediate: true },
    );

    const isSlimMode = computed(() => {
      // On small screen, never use slim mode — always show full menu content
      if (isSmallScreen.value) {
        return false;
      }
      if (isDrawer.value) {
        return !layoutState.sidebarActive && !layoutState.anchored;
      }
      return isSlim.value || isSlimPlus.value;
    });

    const isExpanded = (index) => {
      return expandedSections.value.includes(index);
    };

    const toggleSection = (index) => {
      const idx = expandedSections.value.indexOf(index);
      if (idx === -1) {
        expandedSections.value.push(index);
      } else {
        expandedSections.value.splice(idx, 1);
      }
      saveExpandedState();
    };

    const isActive = (item) => {
      return route.path === item.to;
    };

    const hasActiveChild = (section) => {
      return sectionContainsActivePath(section, route.path);
    };

    const isActiveSection = (item) => {
      return item.to && route.path === item.to;
    };

    // Get label: use translation if labelKey exists, otherwise use label directly
    const getLabel = (item) => {
      if (item.labelKey) {
        return t(item.labelKey);
      }
      return item.label || "";
    };

    return {
      expandedSections,
      isSlimMode,
      isExpanded,
      toggleSection,
      isActive,
      hasActiveChild,
      isActiveSection,
      getLabel,
    };
  },
};
</script>

<style lang="scss" scoped>
.sidebar-menu {
  list-style: none;
  margin: 0;
  padding: 0;
}

.menu-section {
  margin: 0 0.5rem;
}

.menu-section-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.375rem 0;
  width: 100%;
  border: none;
  border-radius: 0 0.5rem 0.5rem 0;
  border-left: 3px solid transparent;
  background-color: transparent;
  color: inherit;
  text-align: left;
  font-size: inherit;
  font-family: inherit;
  transition: all 0.15s ease;
  white-space: nowrap;
  overflow: hidden;

  &:hover {
    background-color: var(--p-content-hover-background);

    .menu-icon {
      color: var(--p-text-color);
    }
  }

  &.has-active-child {
    font-weight: 500;
    border-left-color: var(--p-primary-color);
  }

  &.is-active {
    background-color: var(--p-primary-color);
    border-left-color: var(--p-primary-color);

    .menu-icon {
      color: var(--p-primary-contrast-color);
    }

    .menu-label {
      color: var(--p-primary-contrast-color);
    }

    .menu-arrow {
      color: var(--p-primary-contrast-color);
    }
  }
}

.menu-section-link {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex: 1;
  padding: 0.375rem 0;
  text-decoration: none;
  color: inherit;
  overflow: hidden;

  &:hover {
    text-decoration: none;
  }
}

.menu-toggle-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.375rem;
  border: none;
  background-color: transparent;
  color: inherit;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background-color: rgba(0, 0, 0, 0.05);
  }
}

.menu-arrow {
  margin-left: auto;
  transition: transform 0.3s ease;
  flex-shrink: 0;

  &.expanded {
    transform: rotate(180deg);
  }
}

.menu-subitems {
  list-style: none;
  margin: 0;
  padding: 0;
  padding-left: 0.75rem;
  overflow: hidden;
}

.menu-item {
  margin: 0;
}

.menu-link {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.375rem 0.75rem;
  border-radius: 0 0.5rem 0.5rem 0;
  border-left: 3px solid transparent;
  text-decoration: none;
  color: inherit;
  transition: all 0.15s ease;
  white-space: nowrap;
  overflow: hidden;

  &:hover {
    background-color: var(--p-content-hover-background);

    .menu-icon {
      color: var(--p-text-color);
    }
  }
}

.menu-item.active .menu-link {
  background-color: var(--p-primary-color);
  border-left-color: var(--p-primary-color);
  font-weight: 500;

  .menu-icon {
    color: var(--p-primary-contrast-color);
  }

  .menu-label {
    color: var(--p-primary-contrast-color);
  }
}

.menu-icon {
  font-size: 1.25rem;
  flex-shrink: 0;
  width: 1.5rem;
  text-align: center;
  color: var(--p-text-muted-color);
  transition: color 0.15s ease;
}

.menu-label {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
}

.menu-badge {
  background-color: var(--p-primary-color);
  color: var(--p-primary-contrast-color);
  padding: 0.125rem 0.5rem;
  border-radius: 1rem;
  font-size: 0.75rem;
  font-weight: 600;
  flex-shrink: 0;
}

.menu-item.active .menu-badge {
  background-color: var(--p-primary-contrast-color);
  color: var(--p-primary-color);
}

.menu-item.disabled {
  opacity: 0.5;
  cursor: not-allowed;

  .menu-link {
    cursor: not-allowed;
    pointer-events: none;

    &:hover {
      background-color: transparent;
    }
  }
}

// Submenu transition
.submenu-enter-active,
.submenu-leave-active {
  transition:
    max-height 0.3s ease,
    opacity 0.3s ease;
  max-height: 500px;
  overflow: hidden;
}

.submenu-enter-from,
.submenu-leave-to {
  max-height: 0;
  opacity: 0;
}
</style>
